import { useNotification } from "@context/NotificationContext";
import {
  drybulkCargoOperationsService,
  type CargoOperationDetail,
  type SubmitState,
} from "@services/drybulkCargoOperationsApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import {
  DRYBULK_CARGO_OPS_LANG_BASE,
  DRYBULK_CARGO_OPS_QUERY_KEYS,
  DRYBULK_CARGO_OPS_TELEMETRY,
} from "../constants";
import { problemDetail, problemStatus } from "../utils/problemDetail";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.submit`;

/**
 * DBCO-0031 (R-09) - submit a Draft for approval, one business transaction (the telemetry id is forwarded to
 * the API call). The server's status and Remained replace the cached detail's at once (never derived here);
 * a 409 means the operation moved on, so the page refreshes and the user is told.
 */
export const useSubmitCargoOperation = (
  operationId: string,
  onSubmitted?: (state: SubmitState) => void,
) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const detailKey = DRYBULK_CARGO_OPS_QUERY_KEYS.detail(operationId);

  return useMutation({
    mutationFn: () =>
      runTransaction(
        {
          module: DRYBULK_CARGO_OPS_TELEMETRY.module,
          screen: DRYBULK_CARGO_OPS_TELEMETRY.operationScreen,
          action: "submit_cargo_operation",
          entityType: "cargo_operation",
          entityId: operationId,
        },
        (transactionId) => drybulkCargoOperationsService.submit(operationId, { transactionId }),
      ),
    onSuccess: (state) => {
      queryClient.setQueryData<CargoOperationDetail>(detailKey, (detail) =>
        detail
          ? {
              ...detail,
              status: state.status,
              totalQuantityRemained: state.totalQuantityRemained,
            }
          : detail,
      );
      void queryClient.invalidateQueries({ queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.searchAll() });
      void queryClient.invalidateQueries({ queryKey: detailKey });
      notify({ type: "success", title: t(`${L}.success`) });
      onSubmitted?.(state);
    },
    onError: (error) => {
      if (problemStatus(error) === 409) {
        void queryClient.invalidateQueries({ queryKey: detailKey });
        notify({ type: "warning", title: t(`${L}.conflict`) });
        return;
      }
      notify({
        type: "error",
        title: t("common.error"),
        description: problemDetail(error) ?? t("common.error"),
      });
    },
  });
};
